import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import { format } from 'date-fns'

/**
 * Event date/time fields mean "wall-clock time at the venue" — anchored to the
 * event's IANA timezone. They must never be interpreted in the admin's browser
 * timezone (a Toronto admin editing a Vancouver event would silently shift
 * every date by 3 hours) nor displayed in the server's timezone (the pre-fix
 * behavior: forms showed America/Toronto wall-clocks unlabeled).
 *
 * DST notes: date-fns-tz resolves nonexistent local times (spring-forward gap)
 * and ambiguous ones (fall-back hour) deterministically.
 */

export const DEFAULT_EVENT_TIMEZONE = 'America/Toronto'

/** Django's TIME_ZONE — naive strings sent to filter endpoints are parsed in
 * this zone server-side. Keep in sync with eventpay-backend settings.py. */
const SERVER_TIMEZONE = 'America/Toronto'

/** Display an event's date/time in ITS OWN timezone — for surfaces that list
 * many events at once (e.g. the events list), where each card must read in
 * the event's local zone, not the viewer's. Falls back to the viewer's local
 * zone if the timezone is missing, and returns '' for a missing/invalid date. */
export function formatInEventZone(
  iso: string | null | undefined,
  timeZone: string | null | undefined,
  fmt: string = 'MMM d, yyyy',
): string {
  if (!iso) return ''
  try {
    if (timeZone) return formatInTimeZone(new Date(iso), timeZone, fmt)
    return format(new Date(iso), fmt)
  } catch {
    try {
      return format(new Date(iso), fmt)
    } catch {
      return ''
    }
  }
}

/** Instant (ISO string from the API, any offset) → value for a
 * `datetime-local` input, expressed in the event's timezone. */
export function instantToEventLocal(
  iso: string | null | undefined,
  timeZone: string,
): string {
  if (!iso) return ''
  try {
    return formatInTimeZone(new Date(iso), timeZone, "yyyy-MM-dd'T'HH:mm")
  } catch {
    return ''
  }
}

/** Naive `datetime-local` value, interpreted in the event's timezone → ISO
 * instant for DateTimeField payloads. */
export function eventLocalToInstant(naiveLocal: string, timeZone: string): string {
  return fromZonedTime(naiveLocal, timeZone).toISOString()
}

/** Naive `datetime-local` value in the event's timezone → naive string in the
 * SERVER's timezone, for filter endpoints that parse naive datetimes in
 * Django's TIME_ZONE. Keeps the wire format identical while giving the filter
 * event-timezone semantics. Default format matches `created_at__gte`-style
 * filters; pass e.g. "yyyy-MM-dd'T'HH:mm" or 'yyyy-MM-dd' where the endpoint
 * expects those shapes. */
export function eventLocalToServerNaive(
  naiveLocal: string,
  timeZone: string,
  fmt: string = 'yyyy-MM-dd HH:mm:ss',
): string {
  return formatInTimeZone(fromZonedTime(naiveLocal, timeZone), SERVER_TIMEZONE, fmt)
}

/** "Pacific Time"-style label for annotating date inputs. Falls back to the
 * raw IANA name when the runtime can't produce a generic label. */
export function timeZoneLabel(timeZone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'longGeneric',
    } as Intl.DateTimeFormatOptions).formatToParts(new Date())
    return parts.find((p) => p.type === 'timeZoneName')?.value || timeZone
  } catch {
    return timeZone
  }
}
